// Web3Context.tsx
import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { BaseAdapterSettings, CHAIN_NAMESPACES, WEB3AUTH_NETWORK, type IProvider } from '@web3auth/base';
// Providers
import { EthereumPrivateKeyProvider } from '@web3auth/ethereum-provider';
import { MetamaskAdapter } from '@web3auth/metamask-adapter';
import { Web3Auth, type Web3AuthOptions } from '@web3auth/modal';
import { BUILD_ENV, OPENLOGIN_NETWORK, OpenloginAdapter } from '@web3auth/openlogin-adapter';

import RPC from '@/lib/web3/evm.web3';
import type EthereumRpc from '@/lib/web3/evm.web3';
import Web3AuthCall from '@/lib/web3/Web3AuthCall';
import { SHUFFLE_LOGO } from '@/lib/common/name-space';

import { chainConfig, metamaskAdapter } from '@/lib/web3/config';
import { WalletConnectModal } from '@walletconnect/modal';
import { getWalletConnectV2Settings, WalletConnectV2Adapter } from '@web3auth/wallet-connect-v2-adapter';
import { modalConfigs } from '@/lib/web3Auth/modalConfig';
interface Web3 {
  name: string;
  email: string;
}

export interface Web3ContextType {
  web3authCall: Web3AuthCall | null;
  provider: any;
  user: Web3 | null;
  login: (userData: Web3) => void;
  logout: () => void;
}

// // 폴리곤 테스트 넷
// const chainConfig = {
//   chainId: '0x13882', // amoy 폴리곤 테스트 넷
//   rpcTarget: 'https://polygon-amoy.blockpi.network/v1/rpc/public',
//   chainNamespace: CHAIN_NAMESPACES.EIP155,
//   displayName: 'Amoy',
//   blockExplorerUrl: 'https://amoy.polygonscan.com/',
//   ticker: 'MATIC',
//   tickerName: 'TMATIC',
//   logo: 'https://images.toruswallet.io/eth.svg',
// };

// const metamaskAdapter = new MetamaskAdapter({
//   clientId: process.env.REACT_APP_WEB3_AUTH_CLIENT_ID!,
//   sessionTime: 3600, // 1 hour in seconds
//   web3AuthNetwork: OPENLOGIN_NETWORK.SAPPHIRE_DEVNET,
//   chainConfig: {
//     chainNamespace: CHAIN_NAMESPACES.EIP155,
//     chainId: '0x13882', // amoy 폴리곤 테스트 넷
//     rpcTarget: 'https://polygon-amoy.blockpi.network/v1/rpc/public', // This is the public RPC we have added, please pass on your own endpoint while creating an app
//   },
// });

// const chainConfig = {
//   chainId: '0xaa36a7', // sepolia 이더 테스트 넷
//   rpcTarget: 'https://sepolia.infura.io/v3/',
//   chainNamespace: CHAIN_NAMESPACES.EIP155,
//   displayName: 'Ethereum Testnet',
//   blockExplorerUrl: 'https://sepolia.etherscan.io',
//   ticker: 'ETH',
//   tickerName: 'Sepolia',
//   logo: 'https://images.toruswallet.io/eth.svg',
// };

// const metamaskAdapter = new MetamaskAdapter({
//   clientId: process.env.NEXT_PUBLIC_WEB3_AUTH_CLIENT_ID!,
//   sessionTime: 3600, // 1 hour in seconds
//   web3AuthNetwork: OPENLOGIN_NETWORK.SAPPHIRE_DEVNET,
//   chainConfig: {
//     chainNamespace: CHAIN_NAMESPACES.EIP155,
//     chainId: '0xaa36a7', // sepolia 이더 테스트 넷
//     rpcTarget: 'https://sepolia.infura.io/v3/', // This is the public RPC we have added, please pass on your own endpoint while creating an app
//   },
// });

const privateKeyProvider = new EthereumPrivateKeyProvider({ config: { chainConfig } });

const web3AuthOptions: Web3AuthOptions = {
  clientId: process.env.REACT_APP_WEB3_AUTH_CLIENT_ID!,
  web3AuthNetwork:
    process.env.NODE_ENV === 'development' ? OPENLOGIN_NETWORK.SAPPHIRE_DEVNET : OPENLOGIN_NETWORK.SAPPHIRE_MAINNET,
  sessionTime: 3600,
  uiConfig: {
    uxMode: 'popup',
    appName: 'Assetfi',
    appUrl: process.env.NODE_ENV === 'development' ? 'http://localhost:8083' : 'https://assetfi.ecpay.io',
    theme: {
      primary: '#7ed6df',
    },
    logoLight: SHUFFLE_LOGO,
    logoDark: SHUFFLE_LOGO,
    defaultLanguage: 'en', // en, de, ja, ko, zh, es, fr, pt, nl, tr
    mode: 'auto', // whether to enable dark mode. defaultValue: auto
    useLogoLoader: false,
  },
  privateKeyProvider,

  // clientId: process.env.REACT_APP_WEB3_AUTH_CLIENT_ID as string,
  // web3AuthNetwork:
  //   process.env.NODE_ENV === 'development' ? OPENLOGIN_NETWORK.SAPPHIRE_DEVNET : OPENLOGIN_NETWORK.SAPPHIRE_MAINNET,
  // enableLogging: false,
};

const openloginAdapter = new OpenloginAdapter({
  adapterSettings: {
    clientId: process.env.REACT_APP_WEB3_AUTH_CLIENT_ID!, //Optional - Provide only if you haven't provided it in the Web3Auth Instantiation Code
    network:
      process.env.NODE_ENV === 'development' ? OPENLOGIN_NETWORK.SAPPHIRE_DEVNET : OPENLOGIN_NETWORK.SAPPHIRE_MAINNET,
    uxMode: 'popup',
    whiteLabel: {
      appName: 'W3A Heroes',
      appUrl: 'https://web3auth.io',
      logoLight: 'https://web3auth.io/images/web3auth-logo.svg',
      logoDark: 'https://web3auth.io/images/web3auth-logo---Dark.svg',
      defaultLanguage: 'en', // en, de, ja, ko, zh, es, fr, pt, nl, tr
      mode: 'dark', // whether to enable dark mode. defaultValue: auto
      theme: {
        primary: '#00D1B2',
      },
      useLogoLoader: true,
    },
  },
  // privateKeyProvider,
});

// const openloginAdapter = new OpenloginAdapter({
//   // loginSettings: {
//   //   mfaLevel: 'none',
//   // },
//   adapterSettings: {
//     clientId: process.env.REACT_APP_WEB3_AUTH_CLIENT_ID!,
//     network:
//       process.env.NODE_ENV === 'development' ? OPENLOGIN_NETWORK.SAPPHIRE_DEVNET : OPENLOGIN_NETWORK.SAPPHIRE_MAINNET,
//     buildEnv: process.env.NODE_ENV === 'development' ? BUILD_ENV.DEVELOPMENT : BUILD_ENV.PRODUCTION,

//     uxMode: 'popup', // "redirect" | "popup"
//     whiteLabel: {
//       appName: 'Shuffle',
//       logoLight: 'https://web3auth.io/images/web3authlog.png',
//       logoDark: 'https://web3auth.io/images/web3authlogodark.png',
//       defaultLanguage: 'ko', // en, de, ja, ko, zh, es, fr, pt, nl, tr
//       mode: 'auto', // whether to enable dark, light or auto mode. defaultValue: auto [ system theme]
//     },
//     // mfaSettings: {
//     //   deviceShareFactor: {
//     //     enable: true,
//     //     priority: 1,
//     //     mandatory: true,
//     //   },
//     //   backUpShareFactor: {
//     //     enable: true,
//     //     priority: 2,
//     //     mandatory: false,
//     //   },
//     //   socialBackupFactor: {
//     //     enable: true,
//     //     priority: 3,
//     //     mandatory: false,
//     //   },
//     //   passwordFactor: {
//     //     enable: true,
//     //     priority: 4,
//     //     mandatory: false,
//     //   },
//     // },
//   },
// });

export const Web3Context = createContext<Web3ContextType | undefined>(undefined);

// export const Web3Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
export function Web3Provider({ children }: { children: ReactNode }) {
  const [user, setWeb3] = useState<Web3 | null>(null);
  const [web3authCall, setWeb3authCall] = useState<Web3AuthCall | null>(null);
  const provider = useRef<IProvider | null>(null);

  useEffect(() => {
    const initializeWeb3 = async () => {
      console.log(`initializeWeb3`);
      const web3 = new Web3Auth(web3AuthOptions); // Web3Auth 인스턴스를 생성합니다.
      // web3.configureAdapter(openloginAdapter); // 웹3 인증을 위한 어댑터를 설정합니다.
      web3.configureAdapter(metamaskAdapter);
      // wallet connect scanner
      // web3.addPlugin(walletServicesPlugin);
      // await walletServicesPlugin.showWalletConnectScanner();

      // adding wallet connect v2 adapter
      // const defaultWcSettings = await getWalletConnectV2Settings(
      //   'eip155',
      //   ['80002'],
      //   '2bc1c65293813d37518812ef30760cab',
      // );
      // const walletConnectV2Adapter = new WalletConnectV2Adapter({
      //   ...(web3AuthOptions as BaseAdapterSettings),
      //   adapterSettings: { ...defaultWcSettings.adapterSettings },
      //   loginSettings: { ...defaultWcSettings.loginSettings },
      // });
      // web3.configureAdapter(walletConnectV2Adapter);

      await web3.initModal({
        modalConfig: { ...modalConfigs() },
      }); // 웹3 모달을 초기화합니다.
      // setProvider(web3.provider); // 프로바이더를 설정합니다.
      if (web3.provider) {
        console.log(web3.provider);
        provider.current = web3.provider;
        const re = new Web3AuthCall(web3);
        setWeb3authCall(re);

        // const pools: string[] = await re.getStakingCall().getAllPools();
        // await re.getStakingCall().setStakingContract(pools);

        // const re = new RPC(web3.provider);
        // setRpc(re);
        // const res = new EtherContractCall(re);
        // setCall(res);
      }
      // setRpc(new RPC(provider));
    };
    if (!web3authCall || !provider.current) {
      initializeWeb3(); // 초기화 함수를 호출합니다.
    }

    return () => {};
  }, [web3authCall, provider.current]);
  // useEffect(() => {
  //   (async () => {
  //     if (web3authCall?.connected()) {
  //       console.log('aaaaaaaaaaaaaaaaaaaaaaaaaaa');
  //       const pools: string[] = await web3authCall.getStakingCall().getAllPools();
  //       console.log(`web3-context pools`);
  //       await web3authCall.getStakingCall().setStakingContract(pools);
  //     }
  //   })();
  //   return () => {};
  // }, [web3authCall]);

  // useEffect(() => {
  //   console.log(`empty~~~~~~~~~~~~~~~~~~~~~~`);
  //   console.log(`provider`);
  //   console.log(provider);
  //   console.log(`rpc`);
  //   console.log(rpc);
  //   console.log(`web3auth`);
  //   console.log(web3auth);
  //   return () => {};
  // }, [provider, rpc, web3auth]);

  const getAllPools = async (): Promise<string[]> => {
    const re = await web3authCall?.getStakingCall().getAllPools();
    if (!re?.length) {
      return [];
    }
    return re;
  };
  const login = (userData: Web3) => {
    setWeb3(userData);
  };

  const logout = () => {
    setWeb3(null);
  };

  return (
    <Web3Context.Provider value={{ web3authCall, provider: provider.current, user, login, logout }}>
      {children}
    </Web3Context.Provider>
  );
}

// export const useWeb3 = () => {
//   const context = useContext(Web3Context);
//   if (!context) {
//     throw new Error('useWeb3 must be used within a Web3Provider');
//   }

//   return context;
// };
